import * as React from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SearchIcon from "@mui/icons-material/Search";
import { renewInsurance } from "../../actions/RenewInsurance";
import EmptyState from "../../ui/EmptyState";
import ExpiryChip from "../../ui/ExpiryChip";
import PolicySummary from "../../ui/PolicySummary";
import { formatDate, formatMoney } from "../../utils/premium";

const daysLeft = (date) =>
  Math.max(0, moment(date).startOf("day").diff(moment().startOf("day"), "days"));

export default function RenewInsurance() {
  const user = JSON.parse(secureLocalStorage.getItem("profile"));
  const renew = useSelector((state) => state.renew.insurance) || [];
  const loaded = useSelector((state) => state.loaded.renew);
  const dispatch = useDispatch();
  const [search, setSearch] = React.useState("");
  const [renewData, setRenewData] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const set = (key) => (e) => setRenewData({ ...renewData, [key]: e.target.value });

  const maturityDate =
    renewData?.start && renewData?.tenure
      ? moment(renewData.start).add(renewData.tenure, "days").format("YYYY-MM-DD")
      : "";
  const dailyPremium =
    renewData?.premium && renewData?.tenure ? renewData.premium / renewData.tenure : "";
  const valid =
    renewData && renewData.premium && renewData.start && Number(renewData.tenure) > 0;

  async function sendData() {
    if (!valid) return;
    setSaving(true);
    const saved = await dispatch(
      renewInsurance({
        description: renewData.description,
        purchase_date: renewData.start,
        cost: renewData.premium,
        institution: renewData.institution,
        policy_number: renewData.policy_number,
        policy_type: renewData.policy_type,
        maturity_date: maturityDate,
        tenure: renewData.tenure,
        uploaded_by: user.results.firstName + " " + user.results.lastName,
        daily_premium: dailyPremium,
        approved: "false",
      })
    );
    setSaving(false);
    if (saved) setRenewData(null);
  }

  const openRenew = (row) =>
    setRenewData({
      description: row.description,
      policy_number: row.policy_number,
      institution: row.institution,
      policy_type: row.policy_type,
      premium: row.cost,
      tenure: row.tenure,
      // the new cover starts where the old one ends
      start: moment(row.maturity_date).format("YYYY-MM-DD"),
    });

  const columns = [
    { field: "policy_number", headerName: "Policy number", minWidth: 150, flex: 1 },
    { field: "description", headerName: "Description", minWidth: 180, flex: 1.4 },
    { field: "policy_type", headerName: "Policy type", minWidth: 130, flex: 1 },
    { field: "institution", headerName: "Institution", minWidth: 150, flex: 1.2 },
    {
      field: "cost",
      headerName: "Premium",
      type: "number",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: ({ value }) => formatMoney(value),
    },
    {
      field: "maturity_date",
      headerName: "Ends",
      minWidth: 120,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "days_left",
      headerName: "Time left",
      minWidth: 120,
      valueGetter: ({ row }) => daysLeft(row.maturity_date),
      renderCell: ({ value }) => <ExpiryChip days={value} />,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      minWidth: 110,
      align: "right",
      renderCell: ({ row }) => (
        <Button size="small" variant="outlined" startIcon={<AutorenewIcon />} onClick={() => openRenew(row)}>
          Renew
        </Button>
      ),
    },
  ];

  const query = search.trim().toLowerCase();
  const rows = renew
    .filter(
      (d) =>
        !query ||
        [d.policy_number, d.description, d.institution]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(query))
    )
    .map((d) => ({ ...d, id: d._id || d.policy_number }));

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Policies that are due for renewal. Renewing creates a new policy that starts when the old one ends.
        </Typography>
        <TextField
          placeholder="Search policies..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 300 }, flexShrink: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Box sx={{ height: "calc(100vh - 330px)", minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={!loaded}
          density="compact"
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "days_left", sort: "asc" }] },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{
            noRowsOverlay: () => (
              <EmptyState
                title={query ? "No matching policies" : "Nothing due for renewal"}
                text={query ? "Try another search." : "Policies close to maturity show here."}
              />
            ),
          }}
        />
      </Box>

      <Dialog open={Boolean(renewData)} onClose={saving ? undefined : () => setRenewData(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Renew insurance
          <Typography variant="body2" color="text.secondary">
            {renewData?.policy_number} · {renewData?.institution}
          </Typography>
        </DialogTitle>
        {renewData && (
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Description" fullWidth value={renewData.description || ""} disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Policy type" fullWidth value={renewData.policy_type || ""} onChange={set("policy_type")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Premium" type="number" required fullWidth value={renewData.premium ?? ""} onChange={set("premium")} error={!renewData.premium} inputProps={{ min: 0, step: "0.01" }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="New start date" type="date" required fullWidth value={renewData.start} onChange={set("start")} InputLabelProps={{ shrink: true }} error={!renewData.start} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tenure"
                  type="number"
                  required
                  fullWidth
                  value={renewData.tenure ?? ""}
                  onChange={set("tenure")}
                  error={!(Number(renewData.tenure) > 0)}
                  inputProps={{ min: 1, step: 1 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12}>
                <PolicySummary
                  items={[
                    { label: "New maturity date", value: maturityDate ? formatDate(maturityDate) : "—" },
                    { label: "Daily premium", value: dailyPremium ? formatMoney(dailyPremium) : "—" },
                  ]}
                />
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRenewData(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={sendData} disabled={saving || !valid}>
            {saving ? "Submitting..." : "Submit renewal"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
